/**
 * MetricCard — the workhorse stat tile of the admin dashboard.
 *
 * A thin composition over the shared StatCardFrame + StatCardHead primitives
 * (see StatCard.tsx) so it shares one language with the specialized cards.
 * The NUMBER is the hero; the icon is a quiet real-color tint; an optional
 * trend sparkline is anchored to the card base so tiles align.
 *
 * Accessibility: clickable cards are real buttons (composed aria-label,
 * Enter/Space, focus-visible ring); hover motion respects reduced-motion.
 */

import React from "react";
import { Box, CardContent } from "@mui/material";
import { MetricSparkline } from "@/components/charts";
import { StatCardFrame, StatCardHead, MetricColor } from "./StatCard";

export type { MetricColor };

export interface SparklineData {
  value: number;
  label?: string;
}

export interface MetricCardProps {
  title: string;
  value: number | string;
  icon: React.ReactElement;
  color: MetricColor;
  subtitle?: string;
  tooltip?: React.ReactNode;
  /** sx color token for the value; defaults to text.primary. A value that
   *  is not a number (an unavailable balance) should not wear the weight
   *  of one. */
  valueColor?: string;
  onClick?: () => void;
  sparklineData?: SparklineData[];
  sparklineColor?: "primary" | "success" | "error" | "warning" | "info";
  formatSparklineValue?: (value: number) => string;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  icon,
  color,
  subtitle,
  tooltip,
  valueColor,
  onClick,
  sparklineData,
  sparklineColor,
  formatSparklineValue,
}) => {
  const hasSparkline = !!sparklineData && sparklineData.length > 1;

  return (
    <StatCardFrame
      onClick={onClick}
      ariaLabel={
        onClick
          ? [title, value, subtitle].filter(Boolean).join(", ")
          : undefined
      }
    >
      <CardContent
        sx={{
          p: 2.25,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          "&:last-child": { pb: 2.25 },
        }}
      >
        <StatCardHead
          label={title}
          tooltip={tooltip}
          icon={icon}
          color={color}
          value={value}
          valueColor={valueColor}
          subtitle={subtitle}
        />

        {hasSparkline && (
          <Box sx={{ mt: "auto", pt: 1.5 }}>
            <MetricSparkline
              data={sparklineData}
              height={34}
              color={sparklineColor || "primary"}
              showTrend={false}
              formatValue={formatSparklineValue}
            />
          </Box>
        )}
      </CardContent>
    </StatCardFrame>
  );
};

export default MetricCard;
